import React, { useState } from "react";
import api from "../api/axios";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaBuilding, FaCheck, FaTimes } from "react-icons/fa";
import { useTranslation } from "../context/LanguageContext";
import "../styles/common.css";
import "./AjouterRanch.css";

export default function AjouterRanch() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const [newRanch, setNewRanch] = useState({
    nom: "",
    code: "",
    adresse: "",
    localisation: "",
    telephone: "",
    email: "",
    contact_personne: "",
    description: "",
    actif: true,
  });

  const [emailError, setEmailError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    nom: "",
  });
  const [touchedFields, setTouchedFields] = useState({
    nom: false,
  });

  // Fonction de validation des champs obligatoires
  const validateRequiredField = (name, value) => {
    if (!value || (typeof value === 'string' && value.trim() === "") || value === null) {
      return t.fieldRequired;
    }
    return "";
  };

  // Validation email
  const validateEmail = (email) => {
    if (!email || email.trim() === "") {
      return ""; // Pas d'erreur si le champ est vide (email optionnel)
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return t.invalidEmail;
    }
    return "";
  };

  // Validation d'un champ spécifique
  const validateField = (name, value) => {
    if (name === "nom") {
      return validateRequiredField(name, value);
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;
    
    setNewRanch({
      ...newRanch,
      [name]: fieldValue,
    });

    // Validation email en temps réel
    if (name === "email") {
      const error = validateEmail(fieldValue);
      setEmailError(error);
    }

    // Validation en temps réel si le champ a été touché
    if ((name === "nom") && (touchedFields[name] || fieldErrors[name])) {
      const error = validateField(name, fieldValue);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    // Marquer le champ comme touché
    if (touchedFields.hasOwnProperty(name)) {
      setTouchedFields(prev => ({
        ...prev,
        [name]: true
      }));
    }
    
    // Valider l'email quand on quitte le champ
    if (name === "email") {
      const error = validateEmail(value);
      setEmailError(error);
    }
    
    // Valider le champ obligatoire
    if (name === "nom") {
      const error = validateField(name, value);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Marquer tous les champs obligatoires comme touchés
    const newTouchedFields = {
      nom: true,
    };
    setTouchedFields(newTouchedFields);
    
    // Valider tous les champs obligatoires
    const newFieldErrors = {
      nom: validateField("nom", newRanch.nom),
    };
    setFieldErrors(newFieldErrors);
    
    // Valider l'email avant d'ouvrir le modal
    const emailValidationError = validateEmail(newRanch.email);
    setEmailError(emailValidationError);
    
    // Vérifier s'il y a des erreurs
    const hasErrors = newFieldErrors.nom || emailValidationError;
    
    if (hasErrors) {
      return; // Ne pas ouvrir le modal si il y a des erreurs
    }
    
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setLoading(true);
    try {
      await api.post("/api/ranches/", newRanch);
      setShowConfirmModal(false);
      toast.success("Ranch ajouté avec succès !");
      // Navigate after a short delay to allow the toast to be seen
      setTimeout(() => {
        navigate("/ranches");
      }, 500);
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.message ||
                          Object.values(err.response?.data || {}).flat().join(", ") ||
                          "Erreur lors de l'ajout du ranch !";
      toast.error(errorMessage);
      setShowConfirmModal(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
  };

  // Vérifier si tous les champs obligatoires sont remplis
  const isFormValid = () => {
    if (!newRanch.nom || !newRanch.nom.trim()) {
      return false;
    }
    if (emailError) {
      return false;
    }
    return true;
  };

  return (
    <div className="ajouter-ranch-page">
      <div className="title-badge">
        <FaBuilding className="page-title-icon" aria-hidden="true" />
        <span>{t.addRanchTitle}</span>
      </div>
      
      <form onSubmit={handleSubmit} className="ranch-form">
        {/* Bannière d'information */}
        <div className="form-info-banner">
          <span className="info-icon">ℹ️</span>
          <div className="info-content">
            <strong>{t.requiredFields}</strong>
            <span className="required-indicator">*</span> {t.requiredFieldNote}
          </div>
        </div>

        <div className="form-section-container">
          <div className="form">
            <label htmlFor="nom">
              {t.ranchNameLabel} <span className="required-indicator">*</span>
            </label>
            <input
              type="text"
              id="nom"
              name="nom"
              placeholder={t.ranchNameLabel}
              value={newRanch.nom}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.nom && touchedFields.nom ? "input-error" : ""}
              required
              maxLength={191}
            />
            {fieldErrors.nom && touchedFields.nom && (
              <span className="field-error-message">{fieldErrors.nom}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="code">{t.ranchCode}</label>
            <input
              type="text"
              id="code"
              name="code"
              placeholder={t.ranchCodeOptional}
              value={newRanch.code}
              onChange={handleChange}
              maxLength={50}
            />
          </div>

          <div className="form full-width">
            <label htmlFor="adresse">{t.address}</label>
            <input
              type="text"
              id="adresse"
              name="adresse"
              placeholder={t.fullAddress}
              value={newRanch.adresse}
              onChange={handleChange}
            />
          </div>

          <div className="form">
            <label htmlFor="localisation">{t.location}</label>
            <input
              type="text"
              id="localisation"
              name="localisation"
              placeholder={t.cityRegion}
              value={newRanch.localisation}
              onChange={handleChange}
            />
          </div>

          <div className="form">
            <label htmlFor="telephone">{t.phone}</label>
            <input
              type="text"
              id="telephone"
              name="telephone"
              placeholder="+221 77 123 4567"
              value={newRanch.telephone}
              onChange={handleChange}
            />
          </div>

          <div className="form">
            <label htmlFor="email">{t.email}</label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="Ex: contact@ranch.com"
              value={newRanch.email || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              className={emailError ? "input-error" : ""}
            />
            {emailError && (
              <span className="field-error-message">{emailError}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="contact_personne">{t.contactPerson}</label>
            <input
              type="text"
              id="contact_personne"
              name="contact_personne"
              placeholder={t.contactPersonName}
              value={newRanch.contact_personne}
              onChange={handleChange}
            />
          </div>

          <div className="form full-width">
            <label htmlFor="description">{t.description}</label>
            <textarea
              id="description"
              name="description"
              placeholder="Description du ranch..."
              value={newRanch.description}
              onChange={handleChange}
              rows="4"
            />
          </div>

          <div className="form">
            <label className="checkbox-label">
              <input
                type="checkbox"
                id="actif"
                name="actif"
                checked={newRanch.actif}
                onChange={handleChange}
              />
              <span>{t.active}</span>
            </label>
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="actions">
          <button
            type="submit"
            className="save-but"
            disabled={!isFormValid() || loading}
          >
            <FaCheck />
            <span>{loading ? t.registering : t.register}</span>
          </button>
          <button
            type="button"
            className="cancel-but"
            onClick={() => navigate("/ranches")}
            disabled={loading}
          >
            <FaTimes />
            <span>{t.cancel}</span>
          </button>
        </div>
      </form>

      {/* Modal de confirmation */}
      {showConfirmModal && (
        <div className="confirm-modal-overlay" onClick={handleCancelConfirm}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-header">
              <FaCheck className="confirm-modal-icon" />
              <h3>Confirmer l'ajout du ranch</h3>
            </div>
            <div className="confirm-modal-body">
              <p className="confirm-intro">
                Veuillez vérifier les informations avant de confirmer l'ajout :
              </p>
              <div className="confirm-summary">
                <div className="summary-row">
                  <span className="summary-label">Nom :</span>
                  <span className="summary-value">{newRanch.nom || "Non renseigné"}</span>
                </div>
                {newRanch.code && (
                  <div className="summary-row">
                    <span className="summary-label">Code :</span>
                    <span className="summary-value">{newRanch.code}</span>
                  </div>
                )}
                {newRanch.localisation && (
                  <div className="summary-row">
                    <span className="summary-label">Localisation :</span>
                    <span className="summary-value">{newRanch.localisation}</span>
                  </div>
                )}
                {newRanch.telephone && (
                  <div className="summary-row">
                    <span className="summary-label">Téléphone :</span>
                    <span className="summary-value">{newRanch.telephone}</span>
                  </div>
                )}
                {newRanch.contact_personne && (
                  <div className="summary-row">
                    <span className="summary-label">Contact :</span>
                    <span className="summary-value">{newRanch.contact_personne}</span>
                  </div>
                )}
                <div className="summary-row">
                  <span className="summary-label">Statut :</span>
                  <span className="summary-value">{newRanch.actif ? "Actif" : "Inactif"}</span>
                </div>
              </div>
            </div>
            <div className="confirm-modal-footer">
              <button
                type="button"
                onClick={handleCancelConfirm}
                className="confirm-modal-cancel"
                disabled={loading}
              >
                <FaTimes />
                <span>{t.cancel}</span>
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                className="confirm-modal-confirm"
                disabled={loading}
              >
                <FaCheck />
                <span>{loading ? "Enregistrement..." : "Confirmer"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}
